import { createAuth0Client } from '@auth0/auth0-spa-js';
import { useState, useEffect } from 'react';
import { AuthCtx, CONFIG, api } from './_barrel.js';


        function AuthProvider({ children }) {
            const [client, setClient] = useState(null);
            const [user, setUser] = useState(null);
            // Org-code prompt state: when we can't determine the user's org
            // (no ?tenant= slug and none remembered), we ask for a code rather
            // than silently routing them to the wrong default org (a bounce).
            const [needsOrg, setNeedsOrg] = useState(false);
            const [orgCodeDraft, setOrgCodeDraft] = useState('');
            const [orgError, setOrgError] = useState(null);
            const [token, setToken] = useState(null);
            const [loading, setLoading] = useState(true);
            const [error, setError] = useState(null);

            useEffect(() => {
                (async () => {
                    try {
                        // E2E seam: the smoke suite injects a bearer via sessionStorage
                        // and skips the Auth0 client entirely. Harmless in production —
                        // the API validates every request server-side regardless.
                        let e2eToken = null;
                        try { e2eToken = sessionStorage.getItem('veil_e2e_token'); } catch (e) {}
                        if (e2eToken) {
                            setUser({ name: 'E2E Smoke', email: 'e2e@smoke.test' });
                            setToken(e2eToken);
                            setLoading(false);
                            return;
                        }
                        // Resolve org, in priority order:
                        //   1. ?tenant=... slug in the URL (authoritative; also persisted)
                        //   2. previously-remembered org from a past successful login
                        //   3. configured default (last resort)
                        // This lets a returning user reach their own org without the
                        // slug in the URL, instead of silently falling to the default.
                        const urlParams = new URLSearchParams(window.location.search);
                        const tenantSlug = urlParams.get('tenant');
                        let rememberedOrg = null;
                        try { rememberedOrg = localStorage.getItem('veil_org'); } catch (e) {}
                        // Org context is resolved from the user's EMAIL at sign-in
                        // (see loginWithEmail). On load we only pre-fill a known org
                        // from a ?tenant= slug or a remembered login, so a returning
                        // user is silent; otherwise the login screen asks for the email
                        // and derives the org from it. No org-code / org prompt.
                        let orgId = rememberedOrg || null;
                        if (tenantSlug) {
                            try {
                                const r = await fetch(CONFIG.apiBase + '/api/v1/auth/tenant-org/' + encodeURIComponent(tenantSlug));
                                if (r.ok) {
                                    const body = await r.json();
                                    orgId = body.auth0_org_id;
                                    try { localStorage.setItem('veil_org', orgId); } catch (e) {}
                                }
                            } catch (e) { console.warn('tenant lookup failed', e); }
                        }
                        window.__veilOrgId = orgId;

                        const c = await createAuth0Client({
                            domain: CONFIG.auth0Domain,
                            clientId: CONFIG.auth0ClientId,
                            authorizationParams: {
                                // Always the site root — a fixed, whitelistable
                                // callback. Using pathname here broke login on any
                                // deep link (e.g. an /intake/... URL that nginx
                                // routed to the SPA), sending Auth0 an unregistered
                                // redirect_uri -> "Callback URL mismatch".
                                redirect_uri: window.location.origin + '/',
                                audience: CONFIG.auth0Audience,
                                // Pin the org only when known; otherwise let Auth0 ask.
                                ...(orgId ? { organization: orgId } : {}),
                                scope: 'openid profile email',
                            },
                            cacheLocation: 'localstorage',
                        });
                        setClient(c);

                        if (window.location.search.includes('code=')) {
                            await c.handleRedirectCallback();
                            window.history.replaceState({}, document.title, window.location.pathname);
                        }

                        const authenticated = await c.isAuthenticated();
                        if (authenticated) {
                            const u = await c.getUser();
                            const t = await c.getTokenSilently();
                            // Remember the org Auth0 logged them into, so subsequent
                            // visits pin it and skip the Auth0 org prompt entirely.
                            if (u && u.org_id) { try { localStorage.setItem('veil_org', u.org_id); } catch (e) {} }
                            setUser(u);
                            setToken(t);
                        }
                    } catch (e) {
                        console.error('Auth init failed', e);
                        setError(e.message);
                    } finally {
                        setLoading(false);
                    }
                })();
            }, []);

            const login = async () => {
                if (!client) return;
                await client.loginWithRedirect({
                    authorizationParams: window.__veilOrgId ? { organization: window.__veilOrgId } : {},
                });
            };

            // Email-first sign-in: the user types the email they signed up with;
            // we resolve their organisation from it and send them straight to
            // their own login (email pre-filled). No org code, no org prompt.
            // Returns null on success (redirect follows), or a short error code.
            const loginWithEmail = async (email) => {
                if (!client) return 'not-ready';
                const e = (email || '').trim().toLowerCase();
                if (!e || e.indexOf('@') < 1) return 'invalid';
                let org = null;
                try {
                    const r = await fetch(CONFIG.apiBase + '/api/v1/auth/org-for-email?email=' + encodeURIComponent(e));
                    if (r.ok) { const b = await r.json(); org = b.auth0_org_id; }
                } catch (err) { return 'network'; }
                if (!org) return 'no-account';
                try { localStorage.setItem('veil_org', org); } catch (err) { /* private mode */ }
                await client.loginWithRedirect({
                    authorizationParams: { organization: org, login_hint: e },
                });
                return null;
            };

            const logout = async () => {
                if (!client) return;
                await client.logout({
                    logoutParams: { returnTo: window.location.origin + '/' },
                });
            };
            // Resolve a typed organisation code (the tenant slug) to its Auth0
            // org, persist it, and reload via the working ?tenant= path so login
            // proceeds against the RIGHT org instead of the default (a bounce).
            const resolveOrgCode = async (code) => {
                const slug = (code || '').trim().toLowerCase();
                if (!slug) { setOrgError('Enter your organisation code.'); return; }
                setOrgError(null);
                try {
                    const r = await fetch(CONFIG.apiBase + '/api/v1/auth/tenant-org/' + encodeURIComponent(slug));
                    if (r.ok) {
                        const body = await r.json();
                        if (body && body.auth0_org_id) {
                            try { localStorage.setItem('veil_org', body.auth0_org_id); } catch (e) {}
                            window.location.href = window.location.origin + window.location.pathname + '?tenant=' + encodeURIComponent(slug);
                            return;
                        }
                    }
                    setOrgError('Organisation not found. Check the code and try again.');
                } catch (e) {
                    setOrgError('Could not check that code. Please try again.');
                }
            };

            return (
                <AuthCtx.Provider value={{ client, user, token, login, loginWithEmail, logout, loading, error, needsOrg, orgCodeDraft, setOrgCodeDraft, orgError, resolveOrgCode }}>
                    {children}
                </AuthCtx.Provider>
            );
        }

export { AuthProvider };
